import type {
  QuestionnaireItem,
  QuestionnaireItemEnableWhen,
} from '@medplum/fhirtypes';

import { getAnswerOptionDisplayLabel } from './answerOptionLabel';
import {
  buildEnableWhenForOption,
  isOptionAddonItem,
} from './choiceOptionAddons';
export interface ConditionalValidationIssue {
  path: string;
  message: string;
  severity: 'error';
}

/** Parsed sibling conditional visibility (first enableWhen only). */
export interface ConditionalRule {
  triggerLinkId: string;
  operator: '=';
  answerKey: string;
}

export interface TriggerAnswerOption {
  value: string;
  label: string;
}

export function canBeConditionalTrigger(item: QuestionnaireItem): boolean {
  if (item.type === 'boolean') return true;
  if (item.type === 'choice' || item.type === 'open-choice') {
    return !item.repeats;
  }
  return false;
}

/** Sibling item with enableWhen that is not a per-option nested add-on. */
export function isConditionalSiblingItem(item: QuestionnaireItem): boolean {
  return Boolean(item.enableWhen?.length) && !isOptionAddonItem(item);
}

export function serializeConditionalAnswer(rule: QuestionnaireItemEnableWhen): string {
  if (rule.answerCoding?.code) {
    return `coding:${rule.answerCoding.code}`;
  }
  if (rule.answerString !== undefined) {
    return `string:${rule.answerString}`;
  }
  if (rule.answerBoolean !== undefined) {
    return `boolean:${rule.answerBoolean}`;
  }
  return '';
}

export function getConditionalRule(item: QuestionnaireItem): ConditionalRule | null {
  const condition = item.enableWhen?.[0];
  if (!condition?.question || condition.operator !== '=') return null;
  const answerKey = serializeConditionalAnswer(condition);
  if (!answerKey) return null;
  return {
    triggerLinkId: condition.question,
    operator: '=',
    answerKey,
  };
}

export function getTriggerAnswerOptions(trigger: QuestionnaireItem): TriggerAnswerOption[] {
  if (trigger.type === 'boolean') {
    return [
      { value: 'boolean:true', label: 'Yes' },
      { value: 'boolean:false', label: 'No' },
    ];
  }
  if (trigger.type === 'choice' || trigger.type === 'open-choice') {
    return (trigger.answerOption ?? []).map((opt, idx) => {
      if (opt.valueCoding?.code) {
        return {
          value: `coding:${opt.valueCoding.code}`,
          label: getAnswerOptionDisplayLabel(opt) || `Option ${idx + 1}`,
        };
      }
      const label = getAnswerOptionDisplayLabel(opt) || `Option ${idx + 1}`;
      return { value: `string:${label}`, label };
    });
  }
  return [];
}

export function buildEnableWhenFromAnswerKey(
  trigger: QuestionnaireItem,
  answerKey: string,
): QuestionnaireItemEnableWhen | null {
  const base = { question: trigger.linkId ?? '', operator: '=' as const };
  if (answerKey.startsWith('coding:')) {
    const code = answerKey.slice('coding:'.length);
    const opt = (trigger.answerOption ?? []).find((o) => o.valueCoding?.code === code);
    if (opt?.valueCoding) {
      return buildEnableWhenForOption(trigger, opt, trigger.answerOption!.indexOf(opt));
    }
    return { ...base, answerCoding: { code } };
  }
  if (answerKey.startsWith('string:')) {
    return { ...base, answerString: answerKey.slice('string:'.length) };
  }
  if (answerKey.startsWith('boolean:')) {
    return { ...base, answerBoolean: answerKey === 'boolean:true' };
  }
  return null;
}

export function setConditionalRule(
  item: QuestionnaireItem,
  trigger: QuestionnaireItem | undefined,
  answerKey: string | null,
): QuestionnaireItem {
  if (!trigger || !answerKey) {
    const { enableWhen: _removed, enableBehavior: _behavior, ...rest } = item;
    return rest;
  }
  const enableWhen = buildEnableWhenFromAnswerKey(trigger, answerKey);
  if (!enableWhen) {
    const { enableWhen: _removed, enableBehavior: _behavior, ...rest } = item;
    return rest;
  }
  return { ...item, enableWhen: [enableWhen] };
}

export function listPriorSiblingTriggers(
  siblings: QuestionnaireItem[],
  index: number,
): QuestionnaireItem[] {
  return siblings.slice(0, index).filter(canBeConditionalTrigger);
}

export function formatConditionalBadge(
  item: QuestionnaireItem,
  siblings: QuestionnaireItem[],
): string | null {
  const rule = getConditionalRule(item);
  if (!rule) return null;
  const trigger = siblings.find((s) => s.linkId === rule.triggerLinkId);
  const triggerLabel = trigger?.text?.trim() || 'another question';
  const answerOptions = trigger ? getTriggerAnswerOptions(trigger) : [];
  const answerLabel =
    answerOptions.find((o) => o.value === rule.answerKey)?.label ?? rule.answerKey;
  return `Shows when: ${triggerLabel} = ${answerLabel}`;
}

export function createConditionalFollowUpItem(
  trigger: QuestionnaireItem,
  optionIndex: number,
): QuestionnaireItem {
  const option = trigger.answerOption?.[optionIndex];
  const enableWhen = option
    ? [buildEnableWhenForOption(trigger, option, optionIndex)]
    : trigger.type === 'boolean'
      ? [{ question: trigger.linkId ?? '', operator: '=' as const, answerBoolean: true }]
      : undefined;

  return {
    type: 'text',
    text: 'Follow-up question',
    ...(enableWhen ? { enableWhen } : {}),
  } as QuestionnaireItem;
}

export function countDependentsOnTrigger(
  siblings: QuestionnaireItem[],
  triggerLinkId: string,
): number {
  return siblings.filter(
    (s) => isConditionalSiblingItem(s) && s.enableWhen?.[0]?.question === triggerLinkId,
  ).length;
}

export function clearConditionalRulesReferencing(
  siblings: QuestionnaireItem[],
  triggerLinkId: string,
): QuestionnaireItem[] {
  return siblings.map((s) => {
    if (!isConditionalSiblingItem(s) || s.enableWhen?.[0]?.question !== triggerLinkId) {
      return s;
    }
    const { enableWhen: _removed, enableBehavior: _behavior, ...rest } = s;
    return rest;
  });
}

export function validateConditionalReferences(
  items: QuestionnaireItem[] | undefined,
): ConditionalValidationIssue[] {
  const errors: ConditionalValidationIssue[] = [];

  function validateGroup(children: QuestionnaireItem[], basePath: string): void {
    children.forEach((item, index) => {
      const path = `${basePath}[${index}]`;
      if (isConditionalSiblingItem(item)) {
        const condition = item.enableWhen![0]!;
        const triggerLinkId = condition.question;
        if (!triggerLinkId) {
          errors.push({
            path: `${path}.enableWhen`,
            message: 'Conditional visibility requires a trigger question',
            severity: 'error',
          });
        } else {
          const triggerIdx = children.findIndex((c) => c.linkId === triggerLinkId);
          if (triggerIdx < 0) {
            errors.push({
              path: `${path}.enableWhen`,
              message: `Trigger question "${triggerLinkId}" not found in the same section`,
              severity: 'error',
            });
          } else if (triggerIdx >= index) {
            errors.push({
              path: `${path}.enableWhen`,
              message: 'Follow-up question must appear after its trigger question',
              severity: 'error',
            });
          } else if (!canBeConditionalTrigger(children[triggerIdx]!)) {
            errors.push({
              path: `${path}.enableWhen`,
              message: 'Only single-choice or yes/no questions can trigger conditional follow-ups',
              severity: 'error',
            });
          }
        }
      }
      if (item.type === 'group' && item.item?.length) {
        validateGroup(item.item, `${path}.item`);
      }
    });
  }

  if (items?.length) {
    validateGroup(items, 'item');
  }
  return errors;
}
